<template>
  <div>
    <b-card>
      <!-- Header -->
      <b-row class="mb-3">
        <b-col>
          <h3 class="mb-0">Course Registrations</h3>
          <p class="text-muted">View and manage student course enrollments</p>
        </b-col>
        <b-col class="text-right">
          <b-button variant="success" class="mr-2" @click="openNewRegistrationModal">
            <feather type="user-plus" class="feather-sm"></feather>
            New Registration
          </b-button>
          <b-button variant="primary" @click="refreshRegistrations">
            <feather type="refresh-cw" class="feather-sm"></feather>
            Refresh
          </b-button>
        </b-col>
      </b-row>

      <!-- Filters -->
      <b-row class="mb-3">
        <b-col md="6">
          <b-form-input
            v-model="filter.search"
            placeholder="Search by student name, course, or teacher"
            @input="searchRegistrations"
          ></b-form-input>
        </b-col>
      </b-row>

      <!-- Stats Cards -->
      <b-row class="mb-4">
        <b-col md="4">
          <b-card bg-variant="primary" text-variant="white">
            <h3 class="mb-0">{{ stats.totalRegistrations || 0 }}</h3>
            <p class="mb-0">Total Registrations</p>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card bg-variant="info" text-variant="white">
            <h3 class="mb-0">{{ stats.activeRegistrations || 0 }}</h3>
            <p class="mb-0">Active Enrollments</p>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card bg-variant="success" text-variant="white">
            <h3 class="mb-0">{{ stats.completedRegistrations || 0 }}</h3>
            <p class="mb-0">Left Course</p>
          </b-card>
        </b-col>
      </b-row>

      <!-- Registrations Table -->
      <b-table
        :items="filteredRegistrations"
        :fields="fields"
        :busy="loading"
        :per-page="perPage"
        :current-page="currentPage"
        striped
        hover
        responsive
        show-empty
      >
        <template #table-busy>
          <div class="text-center my-5">
            <b-spinner class="align-middle mr-2"></b-spinner>
            <strong>Loading registrations...</strong>
          </div>
        </template>

        <template #cell(reg_id)="data">
          <code>{{ data.value }}</code>
        </template>

        <template #cell(student)="data">
          <div>
            <div class="font-weight-bold">{{ data.item.student_name || 'N/A' }}</div>
            <small class="text-muted">ID: {{ data.item.user_id }}</small>
          </div>
        </template>

        <template #cell(course)="data">
          <div>
            <div class="font-weight-bold">{{ data.item.course_title || 'N/A' }}</div>
            <small class="text-muted">Teacher: {{ data.item.teacher_name || 'N/A' }}</small>
          </div>
        </template>

        <template #cell(fee)="data">
          PKR {{ Number(data.item.fee || 0).toLocaleString() }}
        </template>

        <template #cell(status)="data">
          <b-badge :variant="getStatusVariant(data.item.payment_status)">
            {{ getStatusLabel(data.item.payment_status) }}
          </b-badge>
        </template>

        <template #cell(registered_at)="data">
          {{ formatDate(data.value) }}
        </template>

        <template #cell(actions)="data">
          <b-button variant="info" size="sm" class="mr-1" @click="viewRegistration(data.item)">
            <feather type="eye" class="feather-sm"></feather>
            View
          </b-button>
          <b-button
            v-if="data.item.payment_status === 1"
            variant="outline-danger"
            size="sm"
            :disabled="removingRegId === data.item.reg_id"
            @click="removeRegistration(data.item)"
          >
            <feather type="user-minus" class="feather-sm"></feather>
            Remove
          </b-button>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-row>
        <b-col md="6">
          <span class="text-muted">
            Showing {{ (currentPage - 1) * perPage + 1 }} to
            {{ Math.min(currentPage * perPage, filteredRegistrations.length) }} of
            {{ filteredRegistrations.length }} registrations
          </span>
        </b-col>
        <b-col md="6">
          <b-pagination
            v-model="currentPage"
            :total-rows="filteredRegistrations.length"
            :per-page="perPage"
            align="right"
            size="sm"
          ></b-pagination>
        </b-col>
      </b-row>
    </b-card>

    <!-- Registration Detail Modal -->
    <b-modal
      v-model="showDetailModal"
      title="Registration Details"
      size="lg"
      ok-only
      ok-variant="secondary"
      ok-title="Close"
    >
      <div v-if="selectedRegistration">
        <b-row>
          <b-col md="6">
            <h6 class="text-muted">Registration ID</h6>
            <p><code>{{ selectedRegistration.reg_id }}</code></p>
          </b-col>
          <b-col md="6">
            <h6 class="text-muted">Status</h6>
            <p>
              <b-badge :variant="getStatusVariant(selectedRegistration.payment_status)">
                {{ getStatusLabel(selectedRegistration.payment_status) }}
              </b-badge>
            </p>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <h6 class="text-muted">Student</h6>
            <p>{{ selectedRegistration.student_name || 'N/A' }}</p>
            <small class="text-muted">ID: {{ selectedRegistration.user_id }}</small>
          </b-col>
          <b-col md="6">
            <h6 class="text-muted">Course</h6>
            <p>{{ selectedRegistration.course_title || 'N/A' }}</p>
            <small class="text-muted">ID: {{ selectedRegistration.course_id }}</small>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <h6 class="text-muted">Teacher</h6>
            <p>{{ selectedRegistration.teacher_name || 'N/A' }}</p>
          </b-col>
          <b-col md="6">
            <h6 class="text-muted">Registration Date</h6>
            <p>{{ formatDate(selectedRegistration.registered_at || selectedRegistration.created_at) }}</p>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <h6 class="text-muted">Fee Paid</h6>
            <p>PKR {{ Number(selectedRegistration.fee || 0).toLocaleString() }}</p>
          </b-col>
          <b-col md="6">
            <h6 class="text-muted">Payment Type</h6>
            <p>{{ selectedRegistration.payment_type || 'N/A' }}</p>
          </b-col>
        </b-row>
      </div>
    </b-modal>

    <!-- New Registration Modal -->
    <b-modal
      v-model="showNewRegistrationModal"
      title="Enroll a Student in a Course"
      @ok.prevent="createRegistration"
      :ok-disabled="!newRegistration.userId || !newRegistration.courseId || creatingRegistration"
      ok-title="Enroll"
      ok-variant="success"
      cancel-title="Cancel"
    >
      <b-form-group label="Student">
        <b-form-select v-model="newRegistration.userId" :options="studentOptions"></b-form-select>
      </b-form-group>
      <b-form-group label="Course">
        <b-form-select v-model="newRegistration.courseId" :options="courseOptions"></b-form-select>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import RegistrationService from '@/services/registration.service';
import UserService from '@/services/user.service';
import CourseService from '@/services/course.service';

export default {
  name: 'RegistrationManagement',
  data() {
    return {
      registrations: [],
      allUsers: [],
      allCourses: [],
      removingRegId: null,
      showNewRegistrationModal: false,
      creatingRegistration: false,
      newRegistration: {
        userId: null,
        courseId: null,
      },
      filteredRegistrations: [],
      loading: false,
      filter: {
        search: '',
      },
      stats: {
        totalRegistrations: 0,
        activeRegistrations: 0,
        completedRegistrations: 0,
      },
      fields: [
        { key: 'reg_id', label: 'Reg ID', sortable: true },
        { key: 'student', label: 'Student', sortable: true },
        { key: 'course', label: 'Course', sortable: true },
        { key: 'fee', label: 'Fee', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'registered_at', label: 'Registered', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      perPage: 20,
      currentPage: 1,
      showDetailModal: false,
      selectedRegistration: null,
    };
  },
  mounted() {
    this.loadRegistrations();
    this.loadAllUsers();
    this.loadAllCourses();
  },
  computed: {
    studentOptions() {
      const options = this.allUsers
        .filter(u => u.user_type === 0)
        .map(u => ({
          value: u.user_id,
          text: `${u.fname || ''} ${u.lname || ''}`.trim() + (u.email ? ` (${u.email})` : ''),
        }));
      return [{ value: null, text: 'Select a student...' }, ...options];
    },
    courseOptions() {
      const options = this.allCourses.map(c => ({ value: c.course_id, text: c.title }));
      return [{ value: null, text: 'Select a course...' }, ...options];
    },
  },
  methods: {
    async loadAllUsers() {
      try {
        const response = await UserService.getAllUsers();
        this.allUsers = response.data;
      } catch (error) {
        // Non-fatal -- the enroll dropdown just stays empty
      }
    },
    async loadAllCourses() {
      try {
        const response = await CourseService.getAllCourses();
        this.allCourses = response.data;
      } catch (error) {
        // Non-fatal -- the enroll dropdown just stays empty
      }
    },
    openNewRegistrationModal() {
      this.newRegistration = { userId: null, courseId: null };
      this.showNewRegistrationModal = true;
    },
    async createRegistration() {
      if (!this.newRegistration.userId || !this.newRegistration.courseId) return;
      this.creatingRegistration = true;
      try {
        await RegistrationService.enrollUser(this.newRegistration.userId, this.newRegistration.courseId);
        this.$bvToast.toast('Student enrolled successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });
        this.showNewRegistrationModal = false;
        this.loadRegistrations();
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to enroll student', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.creatingRegistration = false;
      }
    },
    async removeRegistration(registration) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Remove ${registration.student_name || 'this student'} from "${registration.course_title}"?`,
        {
          title: 'Confirm Removal',
          okVariant: 'danger',
          okTitle: 'Remove',
          cancelTitle: 'Cancel',
        }
      );
      if (!confirmed) return;

      this.removingRegId = registration.reg_id;
      try {
        await RegistrationService.removeUser(registration.user_id, registration.course_id);
        this.$bvToast.toast('Student removed from course', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });
        this.loadRegistrations();
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to remove student', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.removingRegId = null;
      }
    },
    async loadRegistrations() {
      this.loading = true;
      try {
        const response = await RegistrationService.getAllRegistrations();
        this.registrations = response.data;
        this.filteredRegistrations = this.registrations;
        this.calculateStats();
      } catch (error) {
        this.$bvToast.toast('Failed to load registrations', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },
    calculateStats() {
      this.stats.totalRegistrations = this.registrations.length;
      this.stats.activeRegistrations = this.registrations.filter(r => r.payment_status === 1).length;
      this.stats.completedRegistrations = this.registrations.filter(r => r.payment_status === 2).length;
    },
    searchRegistrations() {
      this.filteredRegistrations = this.registrations.filter(reg => {
        const searchLower = this.filter.search.toLowerCase();
        return !this.filter.search ||
          (reg.student_name && reg.student_name.toLowerCase().includes(searchLower)) ||
          (reg.course_title && reg.course_title.toLowerCase().includes(searchLower)) ||
          (reg.teacher_name && reg.teacher_name.toLowerCase().includes(searchLower));
      });
      this.currentPage = 1;
    },
    refreshRegistrations() {
      this.loadRegistrations();
    },
    viewRegistration(registration) {
      this.selectedRegistration = registration;
      this.showDetailModal = true;
    },
    getStatusLabel(paymentStatus) {
      return paymentStatus === 2 ? 'Left Course' : 'Active';
    },
    getStatusVariant(paymentStatus) {
      return paymentStatus === 2 ? 'secondary' : 'success';
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
  },
};
</script>
